import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { inviteService } from '../../services/invite.service';
import { roleService } from '../../services/role.service';
import { departmentService } from '../../services/department.service';
import { FaPlus, FaTrash, FaCopy } from 'react-icons/fa';

const Invites = () => {
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    role: '',
    department: '',
    expiresInDays: 7,
    maxUses: 1
  });

  useEffect(() => {
    fetchInvites();
    fetchRoles();
    fetchDepartments();
  }, []);

  const fetchInvites = async () => {
    try {
      const data = await inviteService.getAllInvites();
      setInvites(data.invites || []);
    } catch (error) {
      toast.error('Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await roleService.getAllRoles();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles');
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAllDepartments();
      setDepartments(data.departments || []);
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await inviteService.createInvite(formData);
      toast.success('Invite code generated!');
      setShowModal(false);
      setFormData({ role: '', department: '', expiresInDays: 7, maxUses: 1 });
      fetchInvites();
    } catch (error) {
      toast.error('Failed to create invite');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invite code?')) return;
    try {
      await inviteService.deleteInvite(id);
      toast.success('Invite deleted');
      fetchInvites();
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Invite Codes</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FaPlus /> Generate Code
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uses</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {invites.map((invite) => (
                <tr key={invite._id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-3 py-1 rounded">{invite.code}</code>
                      <button onClick={() => copyToClipboard(invite.code)} className="text-blue-600">
                        <FaCopy />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">{invite.role?.displayName}</td>
                  <td className="px-6 py-4">{invite.department?.name}</td>
                  <td className="px-6 py-4">{new Date(invite.expiresAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4">{invite.usedCount} / {invite.maxUses}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleDelete(invite._id)} className="text-red-600">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Generate Invite Code</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full border rounded-lg px-3 py-2" required>
                  <option value="">Select Role</option>
                  {roles.map(role => <option key={role._id} value={role._id}>{role.displayName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Department</label>
                <select value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="w-full border rounded-lg px-3 py-2" required>
                  <option value="">Select Department</option>
                  {departments.map(dept => <option key={dept._id} value={dept._id}>{dept.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expires In (Days)</label>
                <input type="number" value={formData.expiresInDays} onChange={(e) => setFormData({...formData, expiresInDays: e.target.value})} className="w-full border rounded-lg px-3 py-2" min="1" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Uses</label>
                <input type="number" value={formData.maxUses} onChange={(e) => setFormData({...formData, maxUses: e.target.value})} className="w-full border rounded-lg px-3 py-2" min="1" required />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg">Generate</button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-300 py-2 rounded-lg">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invites;
